import { useDepartmentText } from 'model/DepartmentTerminology';
import { Box, Group, Loader, Notification, SimpleGrid, Stack, useMantineTheme } from '@mantine/core';
import { useDocumentVisibility } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import FormInputGroup from 'components/forms/FormInputGroup';
import H3Medium from 'components/typography/H3Medium';
import P2Regular from 'components/typography/P2Regular';
import ReadonlyText from 'components/typography/ReadonlyText';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FORM_PAGE_PATH, REVISION_PAGE_PATH, SIGNATURES_PAGE_PATH } from 'routes/paths';
import RevisionStatusIcon from 'components/RevisionStatusIcon';
import P1Medium from 'components/typography/P1Medium';
import { IconEye } from '@tabler/icons-react';
import P2Medium from 'components/typography/P2Medium';
import DateDisplay from 'components/DateDisplay';
import DeviceList from 'components/DeviceList';
import { RevisionListResponse } from 'api/actions/revision-list/revision-list-response';
import { translateSafetyPoint } from 'model/RevisionSafetyPoint';
import {
  ERROR_NOTIFICATION_COLOR,
  INFO_NOTIFICATION_COLOR,
  REVISION_NOT_SIGNED_YET_ERROR_CODE,
  REVISION_SIGNATURE_INVALID_ERROR_CODE,
} from 'utils/constants';
import { nanoid } from 'nanoid';
import Button from 'components/Button';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import useOrganizationModules, {
  getEnabledDocumentKindFilter,
} from 'features/organization-modules/use-organization-modules';

/**
 * Implementation of the Signature Page with required kepMessageUuid prop.
 */
function SignaturePageImpl({ kepMessageUuid }: { kepMessageUuid: string }) {
  const departmentText = useDepartmentText();
  const theme = useMantineTheme();
  const documentState = useDocumentVisibility();
  const { getAction, userId } = useApi();
  const { revisions: revisionsModuleEnabled, forms: formsModuleEnabled } = useOrganizationModules();
  const { isMobile, isTablet } = useResponsiveDesign();

  const [revisions, setRevisions] = useState<RevisionListResponse | null>(null);
  const [refreshKey, setRefreshKey] = useState(nanoid);
  const [showCheckingNotification, setShowCheckingNotification] = useState(false);
  const checkingSignature = useRef(false);

  const kepMessageUuidShort = useMemo(() => kepMessageUuid.slice(0, 8), [kepMessageUuid]);

  const beingSignedRevisions = useMemo(
    () => revisions?.filter((r) => r.revisionStatus.slug === 'being-signed') || [],
    [revisions]
  );

  const maxDocumentValidUntil = useMemo(
    () =>
      revisions?.reduce<string | undefined>((max, revision) => {
        if (!revision.documentValidUntil) return max;
        if (!max) return revision.documentValidUntil;
        return new Date(revision.documentValidUntil) > new Date(max) ? revision.documentValidUntil : max;
      }, undefined),
    [revisions]
  );

  // Fetch all revisions with the specified kepMessageUuid
  useEffect(() => {
    const action = getAction('RevisionList');

    action({
      query: {
        filters: {
          'statusId.in': [9, 10],
          'kepMessageUuid.eq': kepMessageUuid,
          ...getEnabledDocumentKindFilter(revisionsModuleEnabled, formsModuleEnabled),
        },
      },
    })
      .then((documents) =>
        setRevisions(
          documents.filter(({ kind }) => (kind === 'revision' ? revisionsModuleEnabled : formsModuleEnabled))
        )
      )
      .catch(panic);
  }, [formsModuleEnabled, kepMessageUuid, getAction, refreshKey, revisionsModuleEnabled]);

  // Refresh the revisions if they are being signed and the document becomes
  // visible (user returns to the tab).
  useEffect(() => {
    (async () => {
      if (checkingSignature.current || documentState !== 'visible' || beingSignedRevisions.length === 0) {
        return;
      }

      checkingSignature.current = true;
      setShowCheckingNotification(true);

      const getRevision = getAction('RevisionGet');
      const getSignedDoc = getAction('RevisionSaveSignedDoc');

      // Check each revision sequentially
      for (let i = 0; i < beingSignedRevisions.length; i++) {
        const revision = beingSignedRevisions[i];

        try {
          const { revisionStatus } = await getRevision({ parameters: { revisionId: String(revision.revisionId) } });

          if (revisionStatus.slug !== 'being-signed') {
            continue; // The revision is no longer being signed.
          }

          await getSignedDoc({
            parameters: { revisionId: String(revision.revisionId) },
            query: { waitTime: i === 0 ? 10 : 0 }, // Only wait for the first revision - 10 seconds
          });

          setRefreshKey(nanoid()); // Trigger a refresh of the revisions
        } catch (error: any) {
          const code = error?.response?.data?.error?.code;

          if (code === REVISION_NOT_SIGNED_YET_ERROR_CODE) {
            const isTechnician = revision.technician?.userId === userId;

            const message = isTechnician
              ? 'Využite tlačidlo "Podpísať", aby ste dokončili podpísanie revíznych správ pomocou služby BrainIT.'
              : 'Revízny technik ešte nepodpísal revízne správy. Skúste to prosím neskôr alebo kontaktujte revízneho technika.';

            showNotification({
              color: ERROR_NOTIFICATION_COLOR,
              title: 'Dokumenty ešte nie sú podpísané',
              message,
            });
            break; // Stop checking all revisions
          } else if (code === REVISION_SIGNATURE_INVALID_ERROR_CODE) {
            showNotification({
              color: ERROR_NOTIFICATION_COLOR,
              title: 'Podpis revíznych správ je neplatný',
              message:
                'Podpis revíznych správ je neplatný. Skontrolujte platnosť svojho certifikátu alebo kontaktujte administrátora.',
            });
            break; // Stop checking all revisions
          } else {
            // For any other error, report it but continue with other revisions
            panic(error);
          }
        }
      }

      checkingSignature.current = false;
      setShowCheckingNotification(false);
    })();
  }, [documentState, beingSignedRevisions, getAction, userId]);

  const firstRevision = revisions?.[0];

  return (
    <DashboardLayout
      title={`Detail podpisu: ${kepMessageUuidShort ?? 'Načítavam...'}`}
      breadcrumbs={[
        { title: 'Podpisovanie', link: SIGNATURES_PAGE_PATH.original },
        { title: kepMessageUuidShort ?? 'Načítavam...' },
      ]}
    >
      {!!firstRevision && (
        <Stack spacing={24}>
          <FormInputGroup
            groupTitle="Základné informácie"
            stackProps={{ bg: 'white', pb: 40, pr: 40, style: { borderRadius: '4px' } }}
          >
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="KEP UUID" value={kepMessageUuid} />
              <ReadonlyText
                title="Stav"
                value={
                  <RevisionStatusIcon
                    statusId={firstRevision.revisionStatus.revisionStatusId}
                    kind={firstRevision.kind}
                  />
                }
              />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Organizácia" value={firstRevision.organization.organizationName} />
              <ReadonlyText
                title={departmentText('Položka úrovne', 'Stredisko')}
                value={firstRevision.department.departmentName}
              />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Revízny technik" value={firstRevision.technician?.fullName ?? ''} />
              <ReadonlyText
                title="Deadline na prepečiatkovanie"
                value={maxDocumentValidUntil ? new Date(maxDocumentValidUntil).toLocaleDateString() : ''}
              />
            </SimpleGrid>
          </FormInputGroup>

          <Stack pt={24} pb={40} px={isTablet ? 20 : 40} bg="white" style={{ borderRadius: '4px' }} spacing={24}>
            <H3Medium color="gray.7">Revízne správy</H3Medium>
            {isMobile ? (
              <Stack spacing={0}>
                {revisions.map(
                  ({ revisionId, revisionName, devices, finishedAt, safetyPoint, safetyPointLabel, kind }) => (
                    <Stack
                      key={revisionId}
                      spacing={12}
                      p={16}
                      sx={(theme) => ({ borderBottom: `1px solid ${theme.colors.gray[1]}` })}
                    >
                      <ReadonlyText title="ID" value={String(revisionId)} />
                      <ReadonlyText title="Názov" value={revisionName} />
                      <ReadonlyText title="Názov zariadenia" value={<DeviceList devices={devices} />} />
                      <ReadonlyText
                        title="Dátum ukončenia"
                        value={finishedAt ? <DateDisplay date={new Date(finishedAt)} withRelative={false} /> : ''}
                      />
                      <ReadonlyText title="Záver" value={translateSafetyPoint(safetyPoint, safetyPointLabel)} />
                      <Button
                        variant="secondary"
                        component={Link}
                        fullWidth
                        to={
                          kind === 'revision'
                            ? REVISION_PAGE_PATH.insert({ revisionId: String(revisionId) })
                            : FORM_PAGE_PATH.insert({ revisionId: String(revisionId) })
                        }
                        leftIcon={<IconEye stroke="1.5" size={24} />}
                      >
                        Pozrieť
                      </Button>
                    </Stack>
                  )
                )}
              </Stack>
            ) : (
              <Stack spacing={0}>
                <Box
                  className="grid grid-cols-[60px_1fr_350px_180px_200px_120px] gap-4"
                  p={16}
                  bg="gray.0"
                  style={{ borderRadius: '4px 4px 0 0' }}
                >
                  <P1Medium>ID</P1Medium>
                  <P1Medium>Názov</P1Medium>
                  <P1Medium>Názov zariadenia</P1Medium>
                  <P1Medium>Dátum ukončenia</P1Medium>
                  <P1Medium>Záver</P1Medium>
                  <P1Medium>Akcie</P1Medium>
                </Box>
                {revisions.map(
                  ({ revisionId, revisionName, devices, finishedAt, safetyPoint, safetyPointLabel, kind }) => (
                    <Box
                      p={16}
                      key={revisionId}
                      style={{ borderBottom: `1px solid ${theme.colors.gray[1]}` }}
                      className="grid grid-cols-[60px_1fr_350px_180px_200px_120px] items-center gap-4"
                    >
                      <P2Medium>{revisionId}</P2Medium>
                      <P2Regular>{revisionName}</P2Regular>
                      <DeviceList devices={devices} />
                      {finishedAt ? (
                        <Box>
                          <DateDisplay date={new Date(finishedAt)} withRelative={false} />
                        </Box>
                      ) : (
                        <Box data-testid={`empty-signature-revision-${revisionId}-finished-at`} />
                      )}
                      {translateSafetyPoint(safetyPoint, safetyPointLabel) ? (
                        <P2Regular>{translateSafetyPoint(safetyPoint, safetyPointLabel)}</P2Regular>
                      ) : (
                        <Box data-testid={`empty-signature-revision-${revisionId}-safety-point`} />
                      )}
                      <Box>
                        <Button
                          variant="secondary"
                          component={Link}
                          to={
                            kind === 'revision'
                              ? REVISION_PAGE_PATH.insert({ revisionId: String(revisionId) })
                              : FORM_PAGE_PATH.insert({ revisionId: String(revisionId) })
                          }
                          leftIcon={<IconEye stroke="1.5" size={24} />}
                        >
                          Pozrieť
                        </Button>
                      </Box>
                    </Box>
                  )
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      )}

      {showCheckingNotification && (
        <Notification
          color={INFO_NOTIFICATION_COLOR}
          title={
            <Group spacing={8}>
              <Box>Kontrola stavu podpisovania</Box>
              <Loader size={16} />
            </Group>
          }
          style={{ position: 'fixed', bottom: 16, right: 16 }}
          maw={440}
          withCloseButton={false}
        >
          Objekto momentálne komunikuje so službou BrainIT, aby zistil stav podpisovania revíznych správ a overuje
          platnosť podpisov. Táto kontrola môže trvať až niekoľko minút.
        </Notification>
      )}
    </DashboardLayout>
  );
}

/**
 * Page used to display details of a signature (one or multiple revisions with the same KEP message UUID).
 *
 * Displays:
 * - Basic information (kepMessageUuid, organization, department, technician, finishedAt, documentValidUntil)
 * - List of revisions with links to revision details
 */
export default function SignaturePage() {
  const { kepMessageUuid } = useParams<{ kepMessageUuid: string }>();

  if (!kepMessageUuid) {
    return <Navigate to={SIGNATURES_PAGE_PATH.original} replace />;
  }

  return <SignaturePageImpl kepMessageUuid={kepMessageUuid} />;
}
